/**
 * The chance a running NBA prop wins from here (Moss, 5 Nov 2026: "55% at the
 * start ... 1 three on an under 3.5 at the start of the 4th may be 77%").
 *
 * Fitted on 2023-24 and 2024-25 play-by-play and checked on 2025-26, unseen
 * (Documents/nba-research/pbp/live_test.py, live_result.json). At each quarter
 * end the rest of the game is
 *     E[rest] = (a * pre-game expectation + b * tonight's count) * margin factor
 * spread as a negative binomial with variance d * E[rest], then recalibrated.
 * The margin factor is the bench: after Q3 a 20-point game leaves a scorer
 * about two thirds of his usual fourth quarter. On the test season every
 * market lands within 2-5 points of its predicted chance, except points after
 * Q3, which runs 4-8 points high in its 65-85% range.
 *
 * The pre-game expectation is solved from the price taken, so a bet starts at
 * its break-even chance (1 / price) and moves only on what the game does.
 * Between quarter ends the fit is interpolated on the clock.
 */

type Checkpoint = readonly [
  a: number,
  b: number,
  d: number,
  margin: readonly [number, number, number],
  cal: readonly [number, number],
];

interface StatFit {
  readonly d0: number;
  readonly q: readonly [Checkpoint, Checkpoint, Checkpoint];
}

const FIT: Readonly<Record<string, StatFit>> = {
  pts: {
    d0: 3.223,
    q: [
      [0.7149, 0.1017, 3.0, [1.019, 0.985, 0.96], [0.0807, 0.9116]],
      [0.4379, 0.1148, 3.25, [1.034, 0.989, 0.839], [0.0788, 0.9249]],
      [0.1966, 0.0376, 3.75, [1.153, 1.017, 0.671], [0.0417, 0.8841]],
    ],
  },
  reb: {
    d0: 1.52,
    q: [
      [0.6807, 0.1708, 1.5, [1.023, 0.994, 0.982], [0.0257, 0.9701]],
      [0.402, 0.1499, 1.5, [1.043, 0.998, 0.899], [0.0314, 0.981]],
      [0.1818, 0.0487, 1.5, [1.16, 1.038, 0.774], [-0.0338, 0.9055]],
    ],
  },
  ast: {
    d0: 1.455,
    q: [
      [0.6629, 0.2193, 1.25, [1.022, 1.005, 0.983], [0.0185, 0.9217]],
      [0.3871, 0.1635, 1.25, [1.031, 1.015, 0.889], [0.0274, 0.9328]],
      [0.1633, 0.0554, 1.25, [1.119, 1.068, 0.768], [-0.0336, 0.9016]],
    ],
  },
  fg3m: {
    d0: 1.214,
    q: [
      [0.7002, 0.0761, 1.25, [1.019, 1.018, 0.975], [0.0356, 0.9898]],
      [0.4466, 0.0585, 1.25, [1.027, 1.018, 0.903], [0.044, 0.997]],
      [0.1992, 0.0207, 1.25, [1.112, 1.007, 0.826], [-0.0131, 0.9529]],
    ],
  },
  stl: {
    d0: 1.088,
    q: [
      [0.6831, 0.0504, 1.0, [1.033, 1.038, 1.043], [-0.0479, 0.9292]],
      [0.4445, 0.0334, 1.0, [1.036, 1.017, 0.976], [-0.1055, 0.8993]],
      [0.2032, 0.0133, 1.0, [1.092, 1.086, 0.878], [-0.3787, 0.773]],
    ],
  },
  pra: {
    d0: 3.362,
    q: [
      [0.6904, 0.182, 3.0, [1.019, 0.992, 0.968], [0.076, 0.9349]],
      [0.3951, 0.1871, 3.25, [1.032, 0.993, 0.862], [0.0847, 0.9571]],
      [0.178, 0.0566, 3.75, [1.147, 1.03, 0.711], [0.0427, 0.8966]],
    ],
  },
  pr: {
    d0: 3.337,
    q: [
      [0.7015, 0.1441, 3.0, [1.02, 0.991, 0.976], [0.0685, 0.928]],
      [0.4136, 0.1543, 3.25, [1.034, 0.994, 0.866], [0.0698, 0.939]],
      [0.187, 0.048, 4.25, [1.15, 1.03, 0.719], [0.0654, 0.9236]],
    ],
  },
  pa: {
    d0: 3.168,
    q: [
      [0.7007, 0.1514, 3.0, [1.018, 0.988, 0.961], [0.0886, 0.9416]],
      [0.4145, 0.1554, 3.0, [1.031, 0.988, 0.843], [0.0747, 0.9343]],
      [0.1841, 0.0497, 3.75, [1.148, 1.022, 0.672], [0.0658, 0.9228]],
    ],
  },
  ra: {
    d0: 1.73,
    q: [
      [0.6708, 0.2018, 1.75, [1.02, 0.996, 0.974], [0.0539, 0.9873]],
      [0.3838, 0.1807, 1.5, [1.036, 1.001, 0.877], [0.0233, 0.9357]],
      [0.1693, 0.0572, 1.75, [1.149, 1.04, 0.732], [0.0274, 0.9456]],
    ],
  },
};

const STAT_OF: Readonly<Record<string, string>> = {
  PLAYER_POINTS: "pts",
  PLAYER_POINTS_MILESTONE: "pts",
  PLAYER_REBOUNDS: "reb",
  PLAYER_REBOUNDS_MILESTONE: "reb",
  PLAYER_ASSISTS: "ast",
  PLAYER_ASSISTS_MILESTONE: "ast",
  PLAYER_3PM: "fg3m",
  PLAYER_3PM_MILESTONE: "fg3m",
  PLAYER_STEALS: "stl",
  PLAYER_STEALS_MILESTONE: "stl",
  PLAYER_POINTS_REBOUNDS_ASSISTS: "pra",
  PLAYER_PRA: "pra",
  PLAYER_POINTS_REBOUNDS: "pr",
  PLAYER_POINTS_ASSISTS: "pa",
  PLAYER_REBOUNDS_ASSISTS: "ra",
};

function logGamma(x: number): number {
  // Lanczos, g = 7: accurate to ~1e-13 for x > 0.
  const c = [
    0.99999999999980993, 676.5203681218851, -1259.1392167224028,
    771.32342877765313, -176.61502916214059, 12.507343278686905,
    -0.13857109526572012, 9.9843695780195716e-6, 1.5056327351493116e-7,
  ];
  if (x < 0.5)
    return Math.log(Math.PI / Math.sin(Math.PI * x)) - logGamma(1 - x);
  const z = x - 1;
  let sum = c[0]!;
  for (let i = 1; i < 9; i += 1) sum += c[i]! / (z + i);
  const t = z + 7.5;
  return (
    0.5 * Math.log(2 * Math.PI) + (z + 0.5) * Math.log(t) - t + Math.log(sum)
  );
}

/** P(R >= need) for a count with this mean and variance d * mean. */
export function atLeast(mean: number, d: number, need: number): number {
  if (need <= 0) return 1;
  const mu = Math.max(mean, 1e-6);
  let below = 0;
  if (d <= 1.0001) {
    // Poisson.
    let term = Math.exp(-mu);
    for (let k = 0; k < need; k += 1) {
      below += term;
      term *= mu / (k + 1);
    }
  } else {
    const n = mu / (d - 1);
    const p = n / (n + mu);
    const base = logGamma(n) - n * Math.log(p);
    for (let k = 0; k < need; k += 1)
      below += Math.exp(
        logGamma(n + k) - base - logGamma(k + 1) + k * Math.log(1 - p),
      );
  }
  return Math.min(1, Math.max(0, 1 - below));
}

const logit = (p: number) => {
  const q = Math.min(1 - 1e-4, Math.max(1e-4, p));
  return Math.log(q / (1 - q));
};

/** The fit at a share of the game played, interpolated between quarter ends. */
function fitAt(stat: StatFit, played: number, band: 0 | 1 | 2) {
  const start = { a: 1, b: 0, d: stat.d0, m: 1, c0: 0, c1: 1 };
  const points = [
    start,
    ...stat.q.map(([a, b, d, margin, cal]) => ({
      a,
      b,
      d,
      m: margin[band],
      c0: cal[0],
      c1: cal[1],
    })),
  ];
  const end = { ...points[3]!, a: 0, b: 0 };
  const all = [...points, end];
  const at = Math.min(1, Math.max(0, played)) * 4;
  const i = Math.min(3, Math.floor(at));
  const f = at - i;
  const lo = all[i]!;
  const hi = all[i + 1]!;
  const mix = (x: number, y: number) => x + (y - x) * f;
  return {
    a: mix(lo.a, hi.a),
    b: mix(lo.b, hi.b),
    d: mix(lo.d, hi.d),
    m: mix(lo.m, hi.m),
    c0: mix(lo.c0, hi.c0),
    c1: mix(lo.c1, hi.c1),
  };
}

/** The count the bet needs to reach for the Over to land. */
function needed(market: string, line: number): number {
  return market.endsWith("_MILESTONE") && Number.isInteger(line)
    ? line
    : Math.floor(line) + 1;
}

/** The pre-game expectation at which the Over starts at this chance. */
function expectationFor(
  stat: StatFit,
  need: number,
  overChance: number,
): number {
  let lo = 0.01;
  let hi = 120;
  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    if (atLeast(mid, stat.d0, need) < overChance) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

export interface WinChanceInput {
  readonly market: string;
  readonly selection: string;
  readonly line: number | null;
  /** The decimal price taken; its break-even chance is the starting point. */
  readonly price: number;
  /** The stat so far, or null before it has been read. */
  readonly actual: number | null;
  /** Share of regulation played, 0 to 1. */
  readonly played: number | null;
  /** Current margin, either way. */
  readonly margin: number | null;
}

/** Chance this bet wins, 0 to 1, or null for a market this does not model. */
export function winChance(input: WinChanceInput): number | null {
  const fit = FIT[STAT_OF[input.market] ?? ""];
  if (fit === undefined || input.line === null || input.price <= 1) return null;
  const under = input.selection === "UNDER";
  if (!under && input.selection !== "OVER") return null;
  const need = needed(input.market, input.line);
  const start = 1 / input.price;
  const lambda = expectationFor(fit, need, under ? 1 - start : start);
  const x = input.actual ?? 0;
  const played = input.played ?? 0;
  if (played >= 1 || x >= need) {
    const over = x >= need ? 1 : 0;
    return under ? 1 - over : over;
  }
  const band =
    input.margin === null
      ? 0
      : input.margin >= 20
        ? 2
        : input.margin >= 10
          ? 1
          : 0;
  const k = fitAt(fit, played, band);
  const raw = atLeast((k.a * lambda + k.b * x) * k.m, k.d, need - x);
  const over =
    raw > 0.005 && raw < 0.995
      ? 1 / (1 + Math.exp(-(k.c0 + k.c1 * logit(raw))))
      : raw;
  return under ? 1 - over : over;
}

/**
 * The day in one distribution (Moss: "see how the whole day looks in one
 * number"): settled profit plus every open bet as win-or-lose at its chance,
 * treated as independent. Exact over whole dollars.
 */
export function dayOutlook(
  settledCents: number,
  open: readonly {
    readonly chance: number;
    readonly stakeCents: number;
    readonly price: number;
  }[],
): {
  readonly expectedCents: number;
  readonly chanceUp: number;
  readonly low: number;
  readonly high: number;
} {
  let dist = new Map<number, number>([[Math.round(settledCents / 100), 1]]);
  let expected = settledCents;
  for (const bet of open) {
    const win = Math.round((bet.stakeCents * (bet.price - 1)) / 100);
    const loss = -Math.round(bet.stakeCents / 100);
    expected +=
      bet.chance * bet.stakeCents * (bet.price - 1) -
      (1 - bet.chance) * bet.stakeCents;
    const next = new Map<number, number>();
    for (const [value, p] of dist) {
      next.set(value + win, (next.get(value + win) ?? 0) + p * bet.chance);
      next.set(
        value + loss,
        (next.get(value + loss) ?? 0) + p * (1 - bet.chance),
      );
    }
    dist = next;
  }
  const sorted = [...dist.entries()].sort((a, b) => a[0] - b[0]);
  const quantile = (q: number) => {
    let total = 0;
    for (const [value, p] of sorted) {
      total += p;
      if (total >= q) return value * 100;
    }
    return (sorted.at(-1)?.[0] ?? 0) * 100;
  };
  return {
    expectedCents: Math.round(expected),
    chanceUp: sorted
      .filter(([value]) => value > 0)
      .reduce((sum, [, p]) => sum + p, 0),
    low: quantile(0.1),
    high: quantile(0.9),
  };
}
